/**
 * Attendance contracts — V6-04.
 *
 * The shape follows what professional attendance products settled on
 * (§1): **sessions, not a toggle**, because people forget and machines crash;
 * a **computed verdict** rather than a typed-in one; and corrections as
 * requests, so the record of what was changed survives the change.
 */
import { z } from 'zod';
import { objectIdSchema } from './common.js';
/* ── Enums ─────────────────────────────────────────────── */
/**
 * A day's verdict.
 *
 * `non_working` and `holiday` are distinct from `absent` on purpose: nobody
 * should have to explain why they were not at work on a Sunday, and a report
 * that colours those red is a report nobody trusts.
 */
export const ATTENDANCE_DAY_STATUSES = [
    'present',
    'half_day',
    'absent',
    'leave',
    'holiday',
    'non_working',
];
/**
 * Facts about the day that the status alone cannot carry.
 *
 * A day can be `present` *and* `late` *and* `auto_closed` — collapsing those
 * into one enum is how "present" stops meaning anything.
 */
export const ATTENDANCE_FLAGS = ['late', 'early_out', 'auto_closed', 'corrected'];
/** Who or what ended a session. `auto` is always paired with a flag (§3). */
export const ATTENDANCE_END_SOURCES = ['user', 'auto', 'correction'];
export const ATTENDANCE_CORRECTION_STATUSES = ['pending', 'approved', 'rejected'];
/** §3 **[DECIDED defaults]** — stated once, here, and defaulted everywhere. */
export const DEFAULT_ATTENDANCE_RULES = {
    enabled: true,
    expectedStart: '10:00',
    expectedEnd: '19:00',
    graceMinutes: 15,
    fullDayMinutes: 7 * 60,
    halfDayMinutes: 3 * 60,
    autoCloseAt: '23:00',
    autoCloseGraceMinutes: 120,
    remindersEnabled: true,
    halfDayWeekdays: [],
};
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm');
export const attendanceRulesSchema = z
    .object({
    enabled: z.boolean(),
    expectedStart: hhmm,
    expectedEnd: hhmm,
    graceMinutes: z.coerce.number().int().min(0).max(240),
    fullDayMinutes: z.coerce.number().int().min(1).max(24 * 60),
    halfDayMinutes: z.coerce.number().int().min(1).max(24 * 60),
    autoCloseAt: hhmm,
    autoCloseGraceMinutes: z.coerce.number().int().min(0).max(12 * 60),
    remindersEnabled: z.boolean(),
    halfDayWeekdays: z.array(z.number().int().min(0).max(6)).max(7),
})
    .strict()
    .partial()
    .superRefine((value, ctx) => {
    if (value.fullDayMinutes !== undefined &&
        value.halfDayMinutes !== undefined &&
        value.halfDayMinutes > value.fullDayMinutes) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['halfDayMinutes'],
            message: 'A half day cannot need more hours than a full day',
        });
    }
});
/* ── Requests ──────────────────────────────────────────── */
export const startWorkSchema = z
    .object({
    note: z.string().max(300).optional(),
    /**
     * V10 §1 — "End previous session & start", in one request.
     *
     * Deliberately a flag on start rather than a second endpoint: the two
     * halves must not be separable, or a failure between them leaves the person
     * with neither a closed session nor an open one.
     */
    endPrevious: z.boolean().optional(),
})
    .strict();
export const endWorkSchema = z.object({ note: z.string().max(300).optional() }).strict();
/** `YYYY-MM-DD`, in the workspace timezone. Never a Date on the wire. */
export const workDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/** `YYYY-MM`. */
export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');
export const attendanceMonthQuerySchema = z
    .object({ month: monthSchema.optional(), userId: objectIdSchema.optional() })
    .strict();
export const attendanceDayQuerySchema = z
    .object({ date: workDateSchema, userId: objectIdSchema.optional() })
    .strict();
export const correctionRequestSchema = z
    .object({
    date: workDateSchema,
    sessionId: objectIdSchema.optional(),
    /** ISO instants. Both required: a correction states the whole day's shape. */
    proposedStartAt: z.coerce.date(),
    proposedEndAt: z.coerce.date(),
    reason: z.string().min(1).max(500),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.proposedEndAt <= value.proposedStartAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['proposedEndAt'],
            message: 'The end must be after the start',
        });
    }
});
export const correctionDecisionSchema = z
    .object({ decisionNote: z.string().max(500).optional() })
    .strict();
export const listCorrectionsQuerySchema = z
    .object({
    status: z.enum(ATTENDANCE_CORRECTION_STATUSES).optional(),
    userId: objectIdSchema.optional(),
    mine: z
        .union([z.boolean(), z.string()])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true' || v === '1'))
        .optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
export const teamAttendanceQuerySchema = z
    .object({ month: monthSchema.optional(), teamId: objectIdSchema.optional() })
    .strict();
//# sourceMappingURL=attendance.js.map